import { LoadingOutlined } from '@ant-design/icons';
import { Button, Col, Divider, Form, notification, Row, Select } from 'antd';
import PropTypes from 'prop-types';
import React, { memo, useEffect, useMemo, useState } from 'react';
import { useDebouncedCallback } from 'use-debounce';

import { filterOption } from '../../../helpers/form';
import {
  customUpperCase,
  formatarCnpjCpfIMask,
} from '../../../helpers/formatador';
import { geraKey } from '../../../helpers/geradores';
import { useAuth } from '../../../security/authContext';
import apiShipper from '../../../services/apiShipper';
import ListClientes from './ListClientes';

function InfoCliente({
  form,
  cliente,
  setCliente,
  clientesSecundarios,
  setClientesSecundarios,
  clientesPagadores,
  setClientesPagadores,
  isEditingUser,
}) {
  const { setFieldsValue } = form;
  const [apiclientesSecundarios, setApiClientesSecundarios] = useState([]);
  const [apiClientesPagadores, setApiClientesPagadores] = useState([]);
  const [loadingCliente, setLoadingCliente] = useState(false);
  const [loadingClienteSecundarios, setLoadingClienteSecundarios] = useState(
    false
  );
  const [loadingClientesPagadores, setLoadingClientesPagadores] = useState(
    false
  );
  const [selecionandoTodosPagadores, setSelecionandoTodosPagadores] = useState(
    false
  );
  const [userClientesPermitido, setUserClientesPermitido] = useState([]);
  const [disabledField, setDisabledField] = useState(false);

  const { user } = useAuth();

  const isUserPerfilSotranLogado = useMemo(() => user.profile === 'SOTRAN', [
    isEditingUser,
  ]);

  useEffect(() => {
    const getUser = async () => {
      let userClientes = [];

      if (user.cliente_id_secundarios) {
        userClientes = user.cliente_id_secundarios.split(',');
      } else {
        setDisabledField(true);
      }
      userClientes.push(user.cliente_id);
      setUserClientesPermitido(userClientes);
    };
    getUser();
  }, []);

  const handleClientesSearch = async value => {
    let response;
    response = await apiShipper.get('/clientes', {
      params: {
        descricao: value,
      },
    });

    if (!response.data.length) {
      response = await apiShipper.get('/clientes/tmov/raiz/full-search', {
        params: {
          descricao: value,
        },
      });

      const clienteRaiz = response.data[0];

      const hasData =
        response.data.length > 0 &&
        clienteRaiz.cnpj.length > 0 &&
        clienteRaiz.razao_social.length > 0;

      if (hasData) {
        response = await apiShipper.post('/clientes', {
          razao_social: clienteRaiz.razao_social,
          cnpj_resumido: clienteRaiz.cnpj,
          tipo: 'INTEGRACAO',
        });
        response.data = [{ ...response.data }];
      }
    }

    if (!isUserPerfilSotranLogado) {
      let hasViewPermission = false;
      userClientesPermitido.forEach(clienteId => {
        response.data.forEach(res => {
          if (clienteId.toString() === res.id.toString()) {
            hasViewPermission = true;
            response.data = [{ ...res }];
          }
        });
      });

      if (!hasViewPermission) {
        response.data = [];
      }
    }

    return response.data.map(item => ({
      id: item.id,
      cnpj_resumido: item.cnpj_resumido || item.cnpj,
      razao_social: item.razao_social,
    }));
  };

  const getClientesPagadores = async () => {
    setLoadingClientesPagadores(true);

    let pagador = [];

    try {
      const clientes = [...cliente, ...clientesSecundarios];
      const cnpj = clientes.map(({ cnpj_resumido }) => cnpj_resumido);

      if (cnpj.length > 0) {
        const { data } = await apiShipper.get('/clientes/pagadores', {
          params: { cnpj },
        });

        const clientesFilter = data.reduce((clienteLista, clienteCurrent) => {
          const hasCliente = clienteLista.some(
            c => c.cnpj === clienteCurrent.cnpj
          );
          if (!hasCliente) clienteLista.push(clienteCurrent);
          return clienteLista;
        }, []);

        pagador = clientesFilter;
      }
    } catch (err) {
      notification.error({
        message: 'Ops, não foi possível recuperar os clientes',
        description:
          'Por favor, entre em contato com nosso suporte caso o problema persistir.',
      });
    }
    setLoadingClientesPagadores(false);
    return pagador;
  };

  const onFocusClientesPagadores = async () => {
    const data = await getClientesPagadores();

    setApiClientesPagadores(data);
  };

  const searchClientes = async (value, setLoading, setData) => {
    if (!value || value.length < 3) return;

    setLoading(true);
    try {
      const data = await handleClientesSearch(value);
      setData(data);
    } catch (err) {
      notification.error({
        message: 'Ops, não foi possível recuperar os clientes',
        description:
          'Por favor, entre em contato com nosso suporte caso o problema persistir.',
      });
    }
    setLoading(false);
  };

  const onSearchClientesPagador = useDebouncedCallback(async value => {
    searchClientes(value, setLoadingCliente, setCliente);
  }, 1000);

  const onSearchClientesSecundarios = useDebouncedCallback(async value => {
    searchClientes(
      value,
      setLoadingClienteSecundarios,
      setApiClientesSecundarios
    );
  }, 1000);

  const atualizaPagadores = async () => {
    if (!selecionandoTodosPagadores) return;

    const data = await getClientesPagadores();

    setClientesPagadores(data);
  };

  const insertClienteSecundarios = id => {
    setFieldsValue({
      idClienteSecundarios: '',
    });

    if (!id) return;

    const item = apiclientesSecundarios.find(itemData => itemData.id === id);
    const wasSelectedAsPrimaryAlready =
      cliente[0].id.toString() === item.id.toString();
    const existItem = clientesSecundarios.some(
      clienteSec => clienteSec.id === item.id
    );

    if (wasSelectedAsPrimaryAlready || existItem) return;

    setClientesSecundarios([...clientesSecundarios, item]);
  };

  const handleSelectClientePagador = id => {
    if (!id) return;

    form.setFieldsValue({
      idClientesPagadores: '',
    });

    const selectedCliente = clientesPagadores.findIndex(item => item.id === id);
    if (selectedCliente >= 0) return;

    const clientePagador = apiClientesPagadores.find(item => item.id === id);
    setClientesPagadores([...clientesPagadores, clientePagador]);
  };

  const handleClearAllClientePagador = () => {
    setClientesPagadores([]);
    setSelecionandoTodosPagadores(false);
  };

  const handleSelectAllClientePagador = async () => {
    const data = await getClientesPagadores();

    setClientesPagadores(data);
    setSelecionandoTodosPagadores(true);
  };

  const selectOptionCliente = dados => (
    <Select.Option
      key={geraKey(dados.id)}
      title={`${dados.razao_social} - ${dados.cnpj}`}
      value={dados.id}
    >
      {`${dados.razao_social} - ${formatarCnpjCpfIMask(dados.cnpj)}`}
      <br />
      {dados.endereco &&
        `(${dados.uf}) ${customUpperCase(dados.cidade)} - ${dados.endereco} - ${
          dados.bairro === dados.bairro_cliente_endereco
            ? dados.bairro
            : dados.bairro_cliente_endereco
        }`}
    </Select.Option>
  );

  return (
    <>
      <Divider orientation="left">Cliente matriz</Divider>
      <Row gutter={12}>
        <Col span={13} offset={2}>
          <Form.Item
            name="idCliente"
            rules={[
              {
                required: true,
                message: 'Por favor, selecione um cliente!',
              },
            ]}
            label="Cliente"
            labelAlign="right"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <Select
              allowClear
              showSearch
              placeholder="Pesquise um cliente"
              notFoundContent={loadingCliente ? <LoadingOutlined /> : null}
              filterOption={false}
              onSearch={onSearchClientesPagador}
              loading={loadingCliente}
              showAction={['focus', 'click']}
              onChange={e => {
                if (!e) {
                  setCliente([]);
                }
              }}
              // eslint-disable-next-line no-mixed-operators
              disabled={
                (!isUserPerfilSotranLogado && isEditingUser) ||
                (!isUserPerfilSotranLogado && disabledField)
              }
            >
              {cliente.map(_cliente => (
                <Select.Option key={_cliente.id} value={_cliente.id}>
                  {`${_cliente.razao_social} - ${_cliente.cnpj_resumido}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={13} offset={2}>
          <Form.Item
            name="idClienteSecundarios"
            label="CNPJs adicionais"
            labelAlign="right"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 18 }}
          >
            <Select
              showSearch
              showArrow={false}
              placeholder="Pesquise um CNPJ"
              notFoundContent={
                loadingClienteSecundarios ? <LoadingOutlined /> : null
              }
              filterOption={false}
              onSearch={onSearchClientesSecundarios}
              loading={loadingClienteSecundarios}
              showAction={['click']}
              onChange={id => insertClienteSecundarios(id)}
              disabled={
                (!isUserPerfilSotranLogado && isEditingUser) ||
                (!isUserPerfilSotranLogado && disabledField)
              }
            >
              {apiclientesSecundarios.map(dado => (
                <Select.Option key={dado.id} value={dado.id}>
                  {`${dado.razao_social} - ${dado.cnpj_resumido}`}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <ListClientes
        title="CNPJs adicionados:"
        clientes={clientesSecundarios}
        setClientes={setClientesSecundarios}
        atualizaPagadores={atualizaPagadores}
      />

      <Divider orientation="left">Pagadores permitidos</Divider>
      <Row gutter={12}>
        <Col span={13} offset={2}>
          <Form.Item
            name="idClientesPagadores"
            label="Cliente pagador"
            labelAlign="right"
            labelCol={{ span: 6 }}
            wrapperCol={{ span: 19 }}
          >
            <Select
              showSearch
              showArrow={false}
              placeholder="Pesquise um CNPJ"
              filterOption={(value, { title }) => filterOption(value, title, 0)}
              showAction={['click']}
              onChange={id => handleSelectClientePagador(id)}
              notFoundContent={
                loadingClientesPagadores ? <LoadingOutlined /> : null
              }
              onFocus={onFocusClientesPagadores}
              loading={loadingClientesPagadores}
            >
              {apiClientesPagadores.map(dado => selectOptionCliente(dado))}
            </Select>
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={12}>
        <Col span={13} offset={2} align="end">
          <Button onClick={handleSelectAllClientePagador}>
            Selecionar todos
          </Button>
          &nbsp;
          <Button onClick={handleClearAllClientePagador}>Remover todos</Button>
        </Col>
      </Row>
      <ListClientes
        title="Relação CNPJs"
        clientes={clientesPagadores}
        setClientes={setClientesPagadores}
      />
    </>
  );
}

const PropTypesCliente = PropTypes.shape({
  id: PropTypes.number,
  cnpj_resumido: PropTypes.string,
  razao_social: PropTypes.string,
  percentual_tmov: PropTypes.number,
  tarifa_reais_tmov: PropTypes.number,
  pagadores: PropTypes.arrayOf(PropTypes.object),
  created_at: PropTypes.string,
});

InfoCliente.propTypes = {
  form: PropTypes.shape({
    getFieldValue: PropTypes.func,
    setFieldsValue: PropTypes.func,
  }).isRequired,
  cliente: PropTypes.arrayOf(PropTypesCliente).isRequired,
  clientesSecundarios: PropTypes.arrayOf(PropTypesCliente).isRequired,
  clientesPagadores: PropTypes.arrayOf(PropTypesCliente).isRequired,
  setCliente: PropTypes.func.isRequired,
  setClientesSecundarios: PropTypes.func.isRequired,
  setClientesPagadores: PropTypes.func.isRequired,
  isEditingUser: PropTypes.bool.isRequired,
};

export default memo(InfoCliente);
